import React from 'react';
import PropTypes from 'prop-types';
import Parser from 'html-react-parser';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';
import Constants from 'constants/Constants';
import LoginStore from 'stores/LoginStore';
import EditableObjectHandle from '../Common/EditableObjectHandler';
import IntlObject from '../Common/IntlObject';

// 외부 Wrapper - 편집 버튼의 위치 기준점
const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Container = styled.section`
  background-color: ${(props) => props.backgroundColor || '#ffffff'};
  width: 100%;
  /* No more 100vw hacks - parent layout handles full-width display */
`;

const GridContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 300px;

  /* PC: max-width 적용 */
  @media (min-width: 1401px) {
    max-width: ${(props) => props.maxWidth || '1200px'};
    margin: 0 auto;
  }

  /* 1400px 이하: max-width 제한 없음, 꽉 찬 레이아웃 */
  @media (max-width: 1400px) {
    max-width: 100%;
    margin: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageColumn = styled.div`
  position: relative;
  overflow: hidden;
  order: ${(props) => (props.imagePosition === 'right' ? 2 : 1)};

  @media (max-width: 768px) {
    /* 모바일: 이미지 비율 유지를 위해 aspect-ratio 사용 */
    aspect-ratio: 4 / 3;
    min-height: unset;
    order: 1;
  }
`;

const Image = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (max-width: 768px) {
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ContentColumn = styled.div`
  padding: ${(props) => props.padding || '40px'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  order: ${(props) => (props.imagePosition === 'right' ? 1 : 2)};

  @media (max-width: 768px) {
    order: 2;
    padding: 30px 20px;
  }

  & h1, & h2, & h3, & h4, & h5, & h6 {
    margin-top: 0;
  }

  & p {
    line-height: 1.6;
  }

  & ul, & ol {
    padding-left: 20px;
  }
`;

const PlaceholderImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;

  @media (max-width: 768px) {
    position: relative;
    aspect-ratio: 4 / 3;
  }
`;

const PlaceholderContent = styled.div`
  color: #999;
  text-align: center;
  padding: 40px;

  & p {
    margin: 10px 0;
  }
`;

class ImageTextSplit extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      authInfo: LoginStore.authInfo,
    };

    this.onChange = this.onChange.bind(this);
  }

  onChange() {
    this.setState({ authInfo: LoginStore.authInfo });
  }

  componentDidMount() {
    LoginStore.addChangeListener(this.onChange);
  }

  componentWillUnmount() {
    LoginStore.removeChangeListener(this.onChange);
  }

  render() {
    const { id, options, intl } = this.props;
    const currentLocale = intl.locale || 'ko';

    const defaultObjects = {
      imageUrl: '',
      htmlContentKo: '<h2>제목을 입력하세요</h2><p>내용을 입력하세요. 이 영역은 WYSIWYG 에디터로 편집할 수 있습니다.</p>',
      htmlContentEn: '<h2>Enter Title</h2><p>Enter content here. This area can be edited with a WYSIWYG editor.</p>',
      imagePosition: 'left',
      padding: '40px',
      backgroundColor: '#ffffff',
      fullWidth: 'true',
      maxWidth: '',
      customCSS: '',
    };

    // options가 비어있거나 없으면 defaultObjects 사용
    const hasOptions = options && Object.keys(options).length > 0;
    const mergedObjects = hasOptions ? { ...defaultObjects, ...options } : defaultObjects;

    const imageUrl = mergedObjects.imageUrl;
    const htmlContentKo = mergedObjects.htmlContentKo;
    const htmlContentEn = mergedObjects.htmlContentEn;
    const imagePosition = mergedObjects.imagePosition;
    const padding = mergedObjects.padding;
    const backgroundColor = mergedObjects.backgroundColor;
    const fullWidth = mergedObjects.fullWidth !== 'false';
    const maxWidth = mergedObjects.maxWidth;
    const customCSS = mergedObjects.customCSS;

    // 고유 ID 생성 (scoped CSS용)
    const scopeId = `image-text-split-${id}`;

    // Select content based on current locale
    const htmlContent = currentLocale === 'en' ? htmlContentEn : htmlContentKo;

    const isLogin = this.state.authInfo
      ? this.state.authInfo.status === Constants.AUTHED
      : false;

    return (
      <Wrapper id={scopeId}>
        {/* Custom CSS 주입 */}
        {customCSS && (
          <style>
            {`#${scopeId} { ${customCSS} }`}
          </style>
        )}
        <EditableObjectHandle
          id={id}
          objects={mergedObjects}
          defaultObjects={defaultObjects}
          isAdmin={isLogin}
          componentTitle="ImageTextSplit"
        />
        <Container className="its-container" backgroundColor={backgroundColor} fullWidth={fullWidth}>
          <GridContainer className="its-grid" maxWidth={maxWidth}>
            <ImageColumn className="its-image-column" imagePosition={imagePosition}>
              {imageUrl ? (
                <Image className="its-image" src={imageUrl} alt="Section image" />
              ) : (
                <PlaceholderImage>
                  <IntlObject keycode="imagetextsplit.placeholder.image" returnType="span" />
                </PlaceholderImage>
              )}
            </ImageColumn>
            <ContentColumn className="its-content-column" imagePosition={imagePosition} padding={padding}>
              {htmlContent ? (
                Parser(htmlContent)
              ) : (
                <PlaceholderContent>
                  <IntlObject keycode="imagetextsplit.placeholder.content" returnType="p" />
                </PlaceholderContent>
              )}
            </ContentColumn>
          </GridContainer>
        </Container>
      </Wrapper>
    );
  }
}

ImageTextSplit.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  options: PropTypes.shape({
    imageUrl: PropTypes.string,
    htmlContentKo: PropTypes.string,
    htmlContentEn: PropTypes.string,
    imagePosition: PropTypes.oneOf(['left', 'right']),
    padding: PropTypes.string,
    backgroundColor: PropTypes.string,
    fullWidth: PropTypes.string,
    maxWidth: PropTypes.string,
    customCSS: PropTypes.string,
  }),
  intl: PropTypes.object,
};

ImageTextSplit.defaultProps = {
  options: {},
};

export default injectIntl(ImageTextSplit);
